import React from 'react';
import { TableCell, TableRow, Typography, Box, IconButton } from '@mui/material';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Schema } from '../../../../../amplify/data/resource';
import EditIcon from '@mui/icons-material/Edit';

// @ts-ignore
type PaymentPlan = Schema['PaymentPlan']['type'];

interface PaymentPlanItemProps {
  paymentPlan: PaymentPlan;
  onEditClick: (paymentPlan: PaymentPlan) => void;
}

const getChargeModelText = (chargeModel: string) => {
  switch (chargeModel) {
    case 'BY_ROW':
      return 'לפי שורה';
    case 'BY_ID':
      return 'לפי מזהה';
    default:
      return chargeModel;
  }
};

const PaymentPlanItem: React.FC<PaymentPlanItemProps> = ({ paymentPlan, onEditClick }) => {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: he });
    } catch {
      return 'תאריך לא תקין';
    }
  };

  const formatPrice = (price: number) => {
    return `₪${price.toFixed(2)}`;
  };

  const formatDiscount = (percent: number) => {
    return `${percent}%`;
  };

  const formatServices = (services: any) => {
    if (!services || !Array.isArray(services) || services.length === 0) {
      return '-';
    }
    return services.filter(Boolean).join(', ');
  };

  return (
    <TableRow
    sx={{
      backgroundColor: '#F2F4F7',
      borderRadius: '10px',
      p: '20px',
      overflow: 'hidden',
      '&:last-child td, &:last-child th': { border: 0 },
      boxShadow: 'none',
      textAlign: 'right',
      cursor: 'pointer',
    }}
    >
      {/* כותרת */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right', borderTopRightRadius: '10px',
          borderBottomRightRadius: '10px', }}>
        <Typography
          fontSize="14px"
          fontWeight="600"
          fontFamily="AlmoniDL"
          color="#2E3A59"
        >
          {paymentPlan.title}
        </Typography>
      </TableCell>

      {/* מודל חיוב */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right' }}>
        <Typography
          fontSize="14px"
          fontWeight="700"
          fontFamily="AlmoniDL"
          color="#2E3A59"
        >
          {getChargeModelText(paymentPlan.chargeModel || '')}
        </Typography>
      </TableCell>

      {/* מחיר חיוב */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right' }}>
        <Typography
          fontSize="14px"
          fontWeight="600"
          fontFamily="AlmoniDL"
          color="#2E3A59"
        >
          {formatPrice(paymentPlan.chargePrice || 0)}
        </Typography>
      </TableCell>

      {/* מחיר קבוע */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right' }}>
        <Typography
          fontSize="14px"
          fontWeight="600"
          fontFamily="AlmoniDL"
          color="#2E3A59"
        >
          {formatPrice(paymentPlan.fixedPrice || 0)}
        </Typography>
      </TableCell>

      {/* הנחה */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right' }}>
        <Typography
          fontSize="14px"
          fontWeight="700"
          fontFamily="AlmoniDL"
          color="#2E3A59"
        >
          {formatDiscount(paymentPlan.discountPercent || 0)}
        </Typography>
      </TableCell>

      {/* תאריך סיום הנחה */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right' }}>
        <Typography
          fontSize="14px"
          fontWeight="700"
          fontFamily="AlmoniDL"
          color="#2E3A59"
        >
          {paymentPlan.discountExpirationDate ? formatDate(paymentPlan.discountExpirationDate) : '-'}
        </Typography>
      </TableCell>

      {/* שירותים */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right', maxWidth: '200px' }}>
        <Box
          sx={{
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            fontSize="14px"
            fontWeight="700"
            fontFamily="AlmoniDL"
            color="#2E3A59"
          >
            {formatServices(paymentPlan.services)}
          </Typography>
        </Box>
      </TableCell>

      {/* סטטוס */}
      <TableCell sx={{ padding: '15px 20px', textAlign: 'right' }}>
        <Typography
          fontSize="14px"
          fontWeight="700"
          fontFamily="AlmoniDL"
          color="#2E3A59"
        >
          {paymentPlan.active ? 'פעיל' : 'לא פעיל'}
        </Typography>
      </TableCell>

      {/* פעולות */}
      <TableCell sx={{ 
        padding: '15px 20px', 
        textAlign: 'right',
        borderTopLeftRadius: '10px',
        borderBottomLeftRadius: '10px',
      }}>
        <IconButton
          onClick={() => onEditClick(paymentPlan)}
          sx={{
            color: '#2E3A59',
            '&:hover': {
              backgroundColor: 'rgba(46, 58, 89, 0.1)',
            },
          }}
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default PaymentPlanItem; 